import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils/ui";
import { toSentenceCase } from "@/lib/utils/typeUtils";

const EXPERIENCE_LEVELS = {
  beginner: {
    filled: 1,
    iconClassName: "text-blue-600 [.dark_&]:text-blue-400",
  },
  intermediate: {
    filled: 2,
    iconClassName: "text-yellow-500 [.dark_&]:text-yellow-300",
  },
  expert: {
    filled: 3,
    iconClassName: "text-rose-600 [.dark_&]:text-rose-400",
  },
} as const;

type ExperienceLevel = keyof typeof EXPERIENCE_LEVELS;

interface LevelStepsIconProps {
  filled: 1 | 2 | 3;
  className?: string;
}

function LevelStepsIcon({ filled, className }: LevelStepsIconProps) {
  const bars = [
    { x: 1, y: 9, height: 6 },
    { x: 6.25, y: 5, height: 10 },
    { x: 11.5, y: 1, height: 14 },
  ] as const;

  return (
    <svg
      viewBox="0 0 16 16"
      className={className}
      aria-hidden
      fill="none"
    >
      {bars.map((bar, index) => (
        <rect
          key={bar.x}
          x={bar.x}
          y={bar.y}
          width="3.5"
          height={bar.height}
          rx="1.75"
          className={
            index < filled
              ? "fill-current"
              : "fill-muted-foreground opacity-30"
          }
        />
      ))}
    </svg>
  );
}

function isExperienceLevel(value: string): value is ExperienceLevel {
  return value in EXPERIENCE_LEVELS;
}

interface ExperienceLevelIconProps {
  level: string;
  className?: string;
}

export function ExperienceLevelIcon({
  level,
  className,
}: ExperienceLevelIconProps) {
  const config = isExperienceLevel(level.toLowerCase())
    ? EXPERIENCE_LEVELS[level.toLowerCase() as ExperienceLevel]
    : null;

  if (!config) {
    return null;
  }

  return (
    <LevelStepsIcon
      filled={config.filled}
      className={cn("size-3.5 shrink-0", config.iconClassName, className)}
    />
  );
}

interface ExperienceLevelLabelProps {
  level: string;
  className?: string;
}

export function ExperienceLevelLabel({
  level,
  className,
}: ExperienceLevelLabelProps) {
  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      <ExperienceLevelIcon level={level} />
      {toSentenceCase(level)}
    </span>
  );
}

interface ExperienceLevelBadgeProps {
  level: string;
  className?: string;
}

export function ExperienceLevelBadge({
  level,
  className,
}: ExperienceLevelBadgeProps) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 bg-transparent hover:bg-secondary [.dark_&]:bg-secondary [.dark_&]:hover:bg-white/10",
        className,
      )}
    >
      <ExperienceLevelLabel level={level} />
    </Badge>
  );
}
